import { useRef, useState } from 'react';
import {
  deposerDocumentContact,
  formatTaille,
  supprimerDocument,
  renommerDocument,
  urlFichierDocument,
  urlFichierGenere,
} from '../services/documentClientService';
import { formatDate, formatDateTime } from './devisUtils';
import SelectRecherche from '../components/SelectRecherche';
import NomEditable from './NomEditable';
import VignetteFichier from '../components/VignetteFichier';
import '../assets/styles/Drive.css';


const FORMATS_ACCEPTES = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

/* ------------------------------------------------------------------ */
/* Icônes de type                                                      */
/* ------------------------------------------------------------------ */

/**
 * Feuille commune à toutes les icônes : le coin corné dit « document » d'un
 * coup d'œil, la couleur et le sigle disent lequel.
 */
function IconeFichier({ variante, sigle }) {
  return (
    <svg
      className={`drive-icone drive-icone-${variante}`}
      viewBox="0 0 40 48"
      width="40"
      height="48"
      aria-hidden="true"
    >
      <path
        d="M4 4a3 3 0 0 1 3-3h17l13 13v30a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4z"
        className="drive-icone-page"
      />
      <path d="M24 1l13 13H27a3 3 0 0 1-3-3V1z" className="drive-icone-corne" />
      <rect x="4" y="26" width="33" height="14" rx="3" className="drive-icone-bandeau" />
      <text x="20.5" y="36.5" textAnchor="middle" className="drive-icone-sigle">
        {sigle}
      </text>
    </svg>
  );
}

/** Emplacement vide : la pièce est attendue, rien n'est encore arrivé. */
function IconeAttente() {
  return (
    <svg
      className="drive-icone drive-icone-attente"
      viewBox="0 0 40 48"
      width="40"
      height="48"
      aria-hidden="true"
    >
      <path
        d="M4 4a3 3 0 0 1 3-3h17l13 13v30a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="5 4"
      />
      <path
        d="M20 20v14M13 27h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Le type MIME décide de la couleur et du sigle. */
function iconePour(mime) {
  const type = String(mime || '');
  if (type === 'application/pdf') return <IconeFichier variante="pdf" sigle="PDF" />;
  if (type.startsWith('image/')) return <IconeFichier variante="image" sigle="IMG" />;
  if (type.includes('word')) return <IconeFichier variante="doc" sigle="DOC" />;
  return <IconeFichier variante="autre" sigle="FIC" />;
}

/* ------------------------------------------------------------------ */
/* Tuiles                                                              */
/* ------------------------------------------------------------------ */

/** Une pièce reçue : ouvrir, télécharger, renommer, retirer. */
function Tuile({ document: doc, sousTitre, onSupprimer, suppression, onRenommer }) {
  const [renommage, setRenommage] = useState(false);
  return (
    <article className="drive-tuile">
      {/* L'aperçu du fichier lui-même — première page d'un PDF, l'image —
          plutôt qu'un pictogramme : on reconnaît la pièce d'un coup d'œil.
          Cliquer l'ouvre. */}
      <div
        className="drive-tuile-visuel drive-tuile-visuel-apercu"
        role="button"
        tabIndex={0}
        title="Ouvrir"
        onClick={() => window.open(urlFichierDocument(doc.id, { apercu: true }), '_blank')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.open(urlFichierDocument(doc.id, { apercu: true }), '_blank');
          }
        }}
      >
        <VignetteFichier
          url={urlFichierDocument(doc.id, { apercu: true })}
          mime={doc.mime}
          alt={`Aperçu de ${doc.libelle}`}
          secours={iconePour(doc.mime)}
          largeur={232}
        />
      </div>

      <div className="drive-tuile-corps">
        <div className="drive-tuile-nom" title={doc.libelle}>
          <NomEditable
            valeur={doc.libelle}
            edition={renommage}
            onRenommer={(nom) => onRenommer(doc, nom)}
            onFermer={() => setRenommage(false)}
          />
        </div>
        <div className="drive-tuile-meta" title={doc.nomFichier}>
          {doc.nomFichier}
        </div>
        {sousTitre && <div className="drive-tuile-meta">{sousTitre}</div>}
        <div className="drive-tuile-meta">
          {formatTaille(doc.taille)} · {formatDate(doc.deposeLe)}
          <span className="drive-origine">
            {doc.deposePar === 'client' ? 'client' : 'back-office'}
          </span>
        </div>
      </div>

      <div className="drive-tuile-actions">
        <button
          type="button"
          className="drive-action"
          title={`Ouvrir — reçu le ${formatDateTime(doc.deposeLe)}`}
          onClick={() => window.open(urlFichierDocument(doc.id, { apercu: true }), '_blank')}
        >
          Ouvrir
        </button>
        <a className="drive-action" href={urlFichierDocument(doc.id)} download title="Télécharger">
          Télécharger
        </a>
        <button
          type="button"
          className="drive-action"
          onClick={() => setRenommage(true)}
          disabled={renommage || suppression}
          title="Renommer la pièce"
        >
          Renommer
        </button>
        <button
          type="button"
          className="drive-action drive-action-danger"
          onClick={() => onSupprimer(doc)}
          disabled={suppression}
          title="Retirer du dossier"
        >
          {suppression ? 'En cours' : 'Retirer'}
        </button>
      </div>
    </article>
  );
}

/**
 * Un document produit par l'application pour ce client — devis, contrat,
 * convocation, feuille de présence, attestation. Il se consulte et se
 * télécharge ; il ne se renomme ni ne se retire d'ici, c'est sa page
 * d'origine qui le gouverne.
 */
function TuileGeneree({ doc }) {
  const ouvrir = () =>
    window.open(urlFichierGenere(doc.categorie, doc.id, { apercu: true }), '_blank');
  return (
    <article className="drive-tuile">
      <div
        className="drive-tuile-visuel drive-tuile-visuel-apercu"
        role="button"
        tabIndex={0}
        title="Ouvrir"
        onClick={ouvrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            ouvrir();
          }
        }}
      >
        <VignetteFichier
          url={urlFichierGenere(doc.categorie, doc.id, { apercu: true })}
          mime="application/pdf"
          alt={`Aperçu de ${doc.libelle}`}
          secours={iconePour('application/pdf')}
          largeur={232}
        />
      </div>

      <div className="drive-tuile-corps">
        <div className="drive-tuile-nom" title={doc.libelle}>
          {doc.libelle}
        </div>
        {doc.detail && <div className="drive-tuile-meta">{doc.detail}</div>}
        <div className="drive-tuile-meta">
          généré le {formatDate(doc.genereLe)}
          <span className="drive-origine">application</span>
        </div>
      </div>

      <div className="drive-tuile-actions">
        <button type="button" className="drive-action" onClick={ouvrir} title="Ouvrir">
          Ouvrir
        </button>
        <a
          className="drive-action"
          href={urlFichierGenere(doc.categorie, doc.id)}
          download
          title="Télécharger"
        >
          Télécharger
        </a>
      </div>
    </article>
  );
}

/**
 * Emplacement d'une pièce attendue mais pas encore reçue. Il accepte le
 * glisser-déposer : lâcher un fichier dessus le range directement sous cette
 * pièce, sans rien à choisir.
 */
function TuileAttente({ attendu, onDeposer, enCours, onSurvol }) {
  const [survol, setSurvol] = useState(false);
  const input = useRef(null);

  /** Cet emplacement prend la main sur la zone entière tant qu'on le survole. */
  function marquer(actif) {
    setSurvol(actif);
    onSurvol(actif);
  }

  return (
    <article
      className={
        'drive-tuile drive-tuile-attente' +
        (attendu.obligatoire ? ' drive-tuile-requise' : '') +
        (survol ? ' drive-tuile-survol' : '')
      }
      onDragOver={(e) => {
        e.preventDefault();
        // Sans quoi la zone entière se croirait visée et masquerait la tuile.
        e.stopPropagation();
        marquer(true);
      }}
      onDragLeave={() => marquer(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        marquer(false);
        const fichier = e.dataTransfer.files?.[0];
        if (fichier) onDeposer(attendu, fichier);
      }}
    >
      <div className="drive-tuile-visuel">
        <IconeAttente />
      </div>

      <div className="drive-tuile-corps">
        <div className="drive-tuile-nom" title={attendu.libelle}>
          {attendu.libelle}
        </div>
        <div className="drive-tuile-meta">{attendu.formationIntitule}</div>
        <div className="drive-tuile-meta">
          devis {attendu.devisNumero}
          <span
            className={
              'drive-etiquette' +
              (attendu.obligatoire ? ' drive-etiquette-requise' : '')
            }
          >
            {attendu.obligatoire ? 'à fournir' : 'facultatif'}
          </span>
        </div>
      </div>

      <div className="drive-tuile-actions">
        <button
          type="button"
          className="drive-action drive-action-forte"
          onClick={() => input.current?.click()}
          disabled={enCours}
        >
          {enCours ? 'Envoi' : 'Déposer'}
        </button>
        <input
          ref={input}
          type="file"
          accept={FORMATS_ACCEPTES}
          className="drive-input-cache"
          onChange={(e) => {
            const fichier = e.target.files?.[0];
            e.target.value = '';
            if (fichier) onDeposer(attendu, fichier);
          }}
        />
      </div>
    </article>
  );
}

/**
 * Insertion détaillée : on choisit la demande, la pièce visée et, si on le
 * souhaite, le nom sous lequel la ranger.
 *
 * Le glisser-déposer suffit au cas courant, mais il devine le nom d'après le
 * fichier — ce qui donne « scan_20260212 » dans le dossier. Ce formulaire
 * laisse reprendre la main, sans l'imposer : le nom est facultatif, laissé
 * vide c'est le nom du fichier qui sert (le serveur applique le même repli).
 */
function FormulaireDepot({ demandes, demandeId, onDemande, onDepose, onAnnuler }) {
  const [cible, setCible] = useState('');
  const [libelle, setLibelle] = useState('');
  const [fichier, setFichier] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [cleFichier, setCleFichier] = useState(0);

  const demande = demandes.find((d) => String(d.id) === demandeId) || null;
  const attendus = (demande?.devis || []).flatMap((d) =>
    (d.documents?.attendus || [])
      .filter((a) => !a.depot)
      .map((a) => ({ ...a, devisId: d.id, devisNumero: d.numero }))
  );
  const attendu = attendus.find((a) => String(a.id) === cible) || null;

  async function envoyer(e) {
    e.preventDefault();
    if (!fichier || !demande) return;
    setEnCours(true);
    setErreur(null);
    try {
      await onDepose(demande, fichier, {
        formationDocumentId: attendu?.id,
        devisId: attendu?.devisId,
        libelle: attendu ? undefined : libelle.trim() || undefined,
      });
      setFichier(null);
      setLibelle('');
      setCible('');
      setCleFichier((n) => n + 1);
    } catch (err) {
      setErreur(err.message || 'Dépôt impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <form className="param-modal-body drive-formulaire" onSubmit={envoyer}>
      <div className="drive-formulaire-champs">
        {demandes.length > 1 && (
          <label className="drive-champ">
            <span className="drive-champ-libelle">Demande</span>
            <SelectRecherche
              value={demandeId}
              onChange={(e) => {
                onDemande(e.target.value);
                setCible('');
              }}
              required
            >
              <option value="">Choisir…</option>
              {demandes.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.societe_nom} — {formatDate(d.date_soumission)}
                </option>
              ))}
            </SelectRecherche>
          </label>
        )}

        <label className="drive-champ">
          <span className="drive-champ-libelle">Pièce attendue</span>
          <SelectRecherche value={cible} onChange={(e) => setCible(e.target.value)} disabled={!demande}>
            <option value="">Aucune — pièce libre</option>
            {attendus.map((a) => (
              <option key={`${a.devisId}-${a.id}`} value={a.id}>
                {a.libelle} ({a.devisNumero})
              </option>
            ))}
          </SelectRecherche>
        </label>

        <label className="drive-champ">
          <span className="drive-champ-libelle">
            Nom de la pièce <em>facultatif</em>
          </span>
          <input
            type="text"
            placeholder={attendu ? attendu.libelle : 'Sinon, le nom du fichier'}
            value={attendu ? attendu.libelle : libelle}
            onChange={(e) => setLibelle(e.target.value)}
            disabled={Boolean(attendu)}
            maxLength={150}
          />
        </label>

        <label className="drive-champ">
          <span className="drive-champ-libelle">Fichier</span>
          <input
            key={cleFichier}
            type="file"
            accept={FORMATS_ACCEPTES}
            onChange={(e) => setFichier(e.target.files?.[0] || null)}
            required
          />
        </label>

      </div>

      {erreur && <div className="drive-message drive-message-erreur">{erreur}</div>}

      <div className="param-form-actions">
        <button type="button" className="param-btn" onClick={onAnnuler} disabled={enCours}>
          Annuler
        </button>
        <button
          type="submit"
          className="param-btn param-btn-primary"
          disabled={enCours || !fichier || !demande}
        >
          {enCours ? 'Dépôt' : 'Ajouter'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Le drive                                                            */
/* ------------------------------------------------------------------ */

/** Nom de pièce proposé pour un fichier lâché librement : son nom, sans l'extension. */
const nomDepuisFichier = (fichier) =>
  fichier.name.replace(/\.[^.]+$/, '').slice(0, 150) || 'Document';

/**
 * Les pièces d'un client, présentées comme un espace de fichiers : une tuile
 * par document, les pièces encore attendues en emplacements pointillés, et le
 * dépôt par glisser-déposer sur la zone ou sur l'emplacement visé.
 *
 * `pieces` vient du dossier (cf. ClientDossier) : `attendues` porte chacune
 * son `depot` s'il existe, `libres` les fichiers hors pièce attendue.
 */
function ClientDrive({ demandes, pieces, onChange, generes = [] }) {
  // Demande visée par un dépôt libre. Une pièce se range dans une demande
  // précise (contrainte de `documents_client`) : par défaut la plus récente
  // (les demandes arrivent triées ainsi) ; la fenêtre « Ajouter une pièce »
  // permet d'en viser une autre.
  const [demandeId, setDemandeId] = useState(() =>
    demandes.length ? String(demandes[0].id) : ''
  );
  // La fenêtre du dépôt nommé (demande, pièce attendue, nom, fichier).
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [survol, setSurvol] = useState(false);
  // Un emplacement précis est visé : la zone lui laisse la place.
  const [survolTuile, setSurvolTuile] = useState(false);
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [flash, setFlash] = useState(null);
  const [suppressionId, setSuppressionId] = useState(null);

  const demande = demandes.find((d) => String(d.id) === demandeId) || null;
  const recues = pieces.attendues.filter((a) => a.depot);
  const aFournir = pieces.attendues.filter((a) => !a.depot);


  /** Dépôt sous une pièce attendue : sa demande est connue, rien à choisir. */
  // Recherche et filtre : ils balaient les trois familles de tuiles.
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState('tous'); // tous | attente | recues | generes

  const simplifie = (texte) =>
    String(texte || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const mots = simplifie(recherche).split(/\s+/).filter(Boolean);
  const correspond = (texte) => mots.every((m) => simplifie(texte).includes(m));

  async function deposerAttendu(attendu, fichier) {
    setEnCours(attendu.cle);
    setErreur(null);
    setFlash(null);
    try {
      await deposerDocumentContact(attendu.demande.id, fichier, {
        formationDocumentId: attendu.id,
        devisId: attendu.devisId,
      });
      setFlash(`« ${attendu.libelle} » reçu.`);
      await onChange();
    } catch (err) {
      setErreur(err.message || 'Dépôt impossible.');
    } finally {
      setEnCours(null);
    }
  }

  /** Dépôt libre : chaque fichier prend son propre nom comme libellé. */
  async function deposerLibres(fichiers) {
    const liste = [...fichiers];
    if (!liste.length) return;
    if (!demande) {
      setErreur('Choisissez d’abord la demande à laquelle rattacher le fichier.');
      return;
    }

    setErreur(null);
    setFlash(null);
    let deposes = 0;
    try {
      for (const [i, fichier] of liste.entries()) {
        setEnCours(`libre:${i + 1}/${liste.length}`);
        await deposerDocumentContact(demande.id, fichier, { libelle: nomDepuisFichier(fichier) });
        deposes += 1;
      }
      setFlash(`${deposes} fichier${deposes > 1 ? 's' : ''} ajouté${deposes > 1 ? 's' : ''}.`);
    } catch (err) {
      // Les fichiers déjà passés restent déposés : le message le dit plutôt
      // que de laisser croire à un échec complet.
      setErreur(
        `${err.message || 'Dépôt impossible.'}${deposes ? ` (${deposes} déjà ajouté${deposes > 1 ? 's' : ''})` : ''}`
      );
    } finally {
      setEnCours(null);
      await onChange();
    }
  }

  /** Dépôt depuis le formulaire : la pièce et son nom sont explicites. */
  async function deposerNomme(demandeCible, fichier, champs) {
    setErreur(null);
    setFlash(null);
    await deposerDocumentContact(demandeCible.id, fichier, champs);
    setFlash(`« ${champs.libelle || fichier.name} » ajouté au dossier.`);
    await onChange();
  }

  /** Le nouveau nom part au serveur ; la liste se recharge derrière. */
  async function renommer(doc, nom) {
    setErreur(null);
    await renommerDocument(doc.id, nom);
    setFlash(`« ${doc.libelle} » renommé en « ${nom} ».`);
    await onChange();
  }

  async function supprimer(doc) {
    setSuppressionId(doc.id);
    setErreur(null);
    try {
      await supprimerDocument(doc.id);
      setFlash(`« ${doc.libelle} » retiré du dossier.`);
      await onChange();
    } catch (err) {
      setErreur(err.message || 'Suppression impossible.');
    } finally {
      setSuppressionId(null);
    }
  }

  const envoiLibre = typeof enCours === 'string' && enCours.startsWith('libre:');

  if (!demandes.length) {
    return (
      <div className="drive">
        <div className="drive-vide">
          <div className="drive-vide-titre">Aucun dossier à remplir pour l’instant.</div>
          <div className="drive-vide-texte">
            Ce client n’a aucune demande de devis.
          </div>
        </div>
      </div>
    );
  }

  const aFournirAffiches =
    filtre === 'tous' || filtre === 'attente'
      ? aFournir.filter((a) => correspond([a.libelle, a.formationIntitule, a.devisNumero].join(' ')))
      : [];
  const recuesAffichees =
    filtre === 'tous' || filtre === 'recues'
      ? recues.filter((a) =>
          correspond(
            [a.depot.libelle, a.depot.nomFichier, a.formationIntitule, a.devisNumero].join(' ')
          )
        )
      : [];
  const generesAffiches =
    filtre === 'tous' || filtre === 'generes'
      ? generes.filter((g) => correspond([g.libelle, g.detail].join(' ')))
      : [];

  return (
    <div
      className={'drive' + (survol && !survolTuile ? ' drive-survol' : '')}
      onDragOver={(e) => {
        e.preventDefault();
        setSurvol(true);
      }}
      onDragLeave={(e) => {
        // Les survols des enfants remontent : on ne quitte vraiment la zone
        // que lorsque le pointeur sort du conteneur lui-même.
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setSurvol(false);
          setSurvolTuile(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setSurvol(false);
        setSurvolTuile(false);
        deposerLibres(e.dataTransfer.files || []);
      }}
    >
      <div className="drive-barre">
        <div className="drive-compte">
          <strong>{pieces.recues}</strong> fichier{pieces.recues > 1 ? 's' : ''}
          {aFournir.length > 0 && (
            <span className={pieces.manquantes > 0 ? 'drive-compte-alerte' : ''}>
              {' '}
              · {aFournir.length} en attente
            </span>
          )}
        </div>

        <input
          type="search"
          className="devis-search drive-recherche"
          placeholder="Rechercher un document..."
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        <select
          className="drive-filtre"
          value={filtre}
          onChange={(e) => setFiltre(e.target.value)}
          aria-label="Filtrer les documents"
        >
          <option value="tous">Tout</option>
          <option value="attente">En attente</option>
          <option value="recues">Pièces déposées</option>
          <option value="generes">Documents générés</option>
        </select>

        <div className="drive-barre-actions">
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={() => setFormulaireOuvert(true)}
            disabled={envoiLibre}
            title="Déposer un fichier en choisissant la demande, la pièce attendue et son nom"
          >
            Ajouter une pièce
          </button>
        </div>
      </div>

      {erreur && <div className="drive-message drive-message-erreur">{erreur}</div>}
      {flash && !erreur && <div className="drive-message">{flash}</div>}

      {aFournirAffiches.length > 0 && (
        <section className="drive-section">
          <h2 className="drive-section-titre">
            En attente
            <span className="drive-section-compte">{aFournirAffiches.length}</span>
          </h2>
          <div className="drive-grille">
            {aFournirAffiches.map((a) => (
              <TuileAttente
                key={a.cle}
                attendu={a}
                onDeposer={deposerAttendu}
                enCours={enCours === a.cle}
                onSurvol={setSurvolTuile}
              />
            ))}
          </div>
        </section>
      )}

      {recuesAffichees.length > 0 && (
        <section className="drive-section">
          <h2 className="drive-section-titre">
            Pièces déposées
            <span className="drive-section-compte">{recuesAffichees.length}</span>
          </h2>
          <div className="drive-grille">
            {recuesAffichees.map((a) => (
              <Tuile
                key={a.cle}
                document={a.depot}
                sousTitre={`${a.formationIntitule} · devis ${a.devisNumero}`}
                onSupprimer={supprimer}
                suppression={suppressionId === a.depot.id}
                onRenommer={renommer}
              />
            ))}
          </div>
        </section>
      )}

      {generesAffiches.length > 0 && (
        <section className="drive-section">
          <h2 className="drive-section-titre">
            Documents générés pour ce client
            <span className="drive-section-compte">{generesAffiches.length}</span>
          </h2>
          <div className="drive-grille">
            {generesAffiches.map((g) => (
              <TuileGeneree key={`${g.categorie}-${g.id}`} doc={g} />
            ))}
          </div>
        </section>
      )}

      {(recherche || filtre !== 'tous') &&
        aFournirAffiches.length === 0 &&
        recuesAffichees.length === 0 &&
        generesAffiches.length === 0 && (
          <div className="drive-vide">
            <div className="drive-vide-titre">Aucun document ne correspond.</div>
            <div className="drive-vide-texte">Essayez un autre mot, ou élargissez le filtre.</div>
          </div>
        )}

      {!recherche && filtre === 'tous' && pieces.recues === 0 && aFournir.length === 0 && generes.length === 0 && (
        <div className="drive-vide">
          <div className="drive-vide-titre">Ce dossier est vide.</div>
          <div className="drive-vide-texte">
            Glissez des fichiers ici ou utilisez « Ajouter une pièce ».
          </div>
        </div>
      )}

      {formulaireOuvert && (
        <div className="param-modal-overlay" onClick={() => setFormulaireOuvert(false)}>
          <div className="param-modal param-modal-sm drive-modal" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Ajouter une pièce</h2>
              <button
                type="button"
                className="param-modal-close"
                onClick={() => setFormulaireOuvert(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <FormulaireDepot
              demandes={demandes}
              demandeId={demandeId}
              onDemande={setDemandeId}
              onDepose={async (...args) => {
                await deposerNomme(...args);
                setFormulaireOuvert(false);
              }}
              onAnnuler={() => setFormulaireOuvert(false)}
            />
          </div>
        </div>
      )}

      {/* Voile de dépôt : il ne se montre que pendant le survol d'un fichier. */}
      <div className="drive-voile" aria-hidden="true">
        <div className="drive-voile-texte">
          Déposez pour ajouter au dossier
        </div>
      </div>
    </div>
  );
}

export default ClientDrive;
